import { memo, useMemo } from "react";
import { FlaskConical, Plus, Thermometer } from "lucide-react";
import PlanSparkline from "components/experiment/designer/PlanSparkline";
import { calibrationPlanSeries, formatDuration, planMinutes, stressPlanSeries } from "utils/planPreview";
import { HARSHNESS_STYLE, harshness } from "utils/calibrationCampaign";

const TABS = [
  { id: "all", label: "All" },
  { id: "stress", label: "Stress runs" },
  { id: "calibration", label: "Calibration" },
];

export const BLANK_STRESS = {
  key: "blank-stress", kind: "stress", name: "", description: "Start from one step and build your own.",
  steps: [{ kind: "hold", temp_c: 25, rh_pct: 60, duration_seconds: 3600 }], water_events: [], blank: true,
};
export const BLANK_CALIBRATION = {
  key: "blank-calibration", kind: "calibration", name: "", description: "Start from one condition and add your own.",
  conditions: [{ temp_c: 30, rh_pct: 60 }], ladder: {}, blank: true,
};

// Built-in presets (from the backend) and the saved ones (StressProfile rows) in one shape.
export function savedToPreset(profile) {
  return profile.kind === "calibration"
    ? { key: `saved-${profile.id}`, kind: "calibration", name: profile.name, description: profile.description,
        conditions: profile.conditions, ladder: profile.ladder || {}, stability: profile.stability || {},
        profileId: profile.id, saved: true }
    : { key: `saved-${profile.id}`, kind: "stress", name: profile.name, description: profile.description,
        steps: profile.steps, water_events: profile.water_events || [], profileId: profile.id, saved: true };
}

function presetPoints(preset, defaultLadder) {
  if (preset.kind === "calibration") {
    const ladder = preset.ladder && preset.ladder.levels_cm ? preset.ladder : defaultLadder || {};
    return calibrationPlanSeries(preset.conditions || [], ladder);
  }
  return stressPlanSeries(preset.steps || [], preset.water_events || [], { startLevelCm: preset.water_events?.length ? 20 : null });
}

function HarshnessChips({ conditions }) {
  const counts = { mild: 0, moderate: 0, harsh: 0 };
  (conditions || []).forEach((c) => {
    const level = harshness(c);
    if (level) counts[level] += 1;
  });
  return (
    <div className="flex flex-wrap gap-1">
      {Object.entries(counts)
        .filter(([, n]) => n)
        .map(([level, n]) => (
          <span key={level} className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${HARSHNESS_STYLE[level]}`}>
            {n} {level}
          </span>
        ))}
    </div>
  );
}

const PresetCard = memo(function PresetCard({ preset, selected, onSelect, defaultLadder }) {
  const points = useMemo(() => presetPoints(preset, defaultLadder), [preset, defaultLadder]);
  const Icon = preset.blank ? Plus : preset.kind === "calibration" ? FlaskConical : Thermometer;
  const steps = preset.kind === "calibration" ? `${(preset.conditions || []).length} conditions` : `${(preset.steps || []).length} steps`;
  return (
    <button
      type="button"
      onClick={() => onSelect(preset)}
      aria-pressed={selected}
      className={`flex h-full flex-col gap-2 rounded-2xl border p-3 text-left transition ${
        selected ? "border-blue-600 bg-blue-50 ring-2 ring-blue-200" : "border-slate-200 bg-white hover:border-blue-300"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex items-center gap-2 text-sm font-bold text-slate-900">
          <Icon className="h-4 w-4 shrink-0 text-blue-700" />
          {preset.blank ? (preset.kind === "calibration" ? "Blank calibration" : "Blank stress run") : preset.name}
        </span>
        <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
          {preset.saved ? "Yours" : preset.blank ? "New" : preset.kind === "calibration" ? "Calibration" : "Stress"}
        </span>
      </div>
      {!preset.blank && <PlanSparkline points={points} />}
      <p className="line-clamp-3 text-xs text-slate-600">{preset.description || " "}</p>
      {!preset.blank && (
        <div className="mt-auto flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
          <span>{steps}</span>
          <span>· about {formatDuration(planMinutes(points) * 60)}</span>
          {preset.kind === "calibration" && <HarshnessChips conditions={preset.conditions} />}
        </div>
      )}
    </button>
  );
});

/**
 * Step 1 of the designer: pick what to run. Built-in presets, the team's saved ones ("Yours") and a blank start,
 * filtered by kind. Each card shows a tiny picture of its plan and how long it takes.
 */
export default function PresetGallery({ builtins, saved, selectedKey, onSelect, tab, onTabChange, defaultLadder }) {
  const all = useMemo(
    () => [...(builtins || []), ...(saved || []).map(savedToPreset), BLANK_STRESS, BLANK_CALIBRATION],
    [builtins, saved]
  );
  const shown = tab === "all" ? all : all.filter((p) => p.kind === tab);
  return (
    <div className="flex flex-col gap-3">
      <div role="tablist" aria-label="Kind of experiment" className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => onTabChange(t.id)}
            className={`rounded-full px-3 py-1.5 text-xs font-bold ${tab === t.id ? "bg-blue-700 text-white" : "bg-slate-100 text-slate-600"}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {shown.map((preset) => (
          <PresetCard key={preset.key} preset={preset} selected={preset.key === selectedKey} onSelect={onSelect} defaultLadder={defaultLadder} />
        ))}
      </div>
    </div>
  );
}
